import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash } from "@phosphor-icons/react";

export default function HolidaysDialog({ year, onClose }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/payroll/attendance/holidays", { params: { year } }); setList(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.date) return toast.error("Pick a date");
    setBusy(true);
    try { await api.post("/payroll/attendance/holidays", form); setForm({ date: "", name: "" }); await load(); toast.success("Holiday added — marked H (paid) for everyone"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const remove = async (h) => {
    try { await api.delete(`/payroll/attendance/holidays/${h.id}`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-[520px]" data-testid="holidays-dialog">
        <DialogHeader><DialogTitle className="text-sm">Paid Holidays · {year}</DialogTitle></DialogHeader>
        <div className="flex gap-2 items-end">
          <label className="block flex-1">
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">Date</div>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-8 text-[12px]" data-testid="holiday-date" />
          </label>
          <label className="block flex-[2]">
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">Name</div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Diwali" className="h-8 text-[12px]" data-testid="holiday-name" onKeyDown={(e) => e.key === "Enter" && add()} />
          </label>
          <Button size="sm" className="h-8 bg-[#0F172A] text-white hover:bg-slate-800" onClick={add} disabled={busy} data-testid="holiday-add-btn"><Plus size={12} className="mr-1" /> Add</Button>
        </div>
        <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[50vh] overflow-y-auto text-[11.5px]" data-testid="holiday-list">
          {list.length === 0 && <div className="text-center text-slate-400 py-6">No holidays for {year} yet. Sundays are already paid weekly offs.</div>}
          {list.map((h) => (
            <div key={h.id} className="flex items-center gap-3 px-3 py-1.5" data-testid={`holiday-row-${h.date}`}>
              <span className="font-mono-num text-slate-700 w-24">{h.date}</span>
              <span className="flex-1 text-slate-900">{h.name}</span>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => remove(h)} data-testid={`holiday-delete-${h.date}`}><Trash size={12} /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
